import {useParams} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {
    useCreateBranchContactMutation,
    useGetBranchContactByIdQuery, useLazyGetBranchContactByContactIdQuery, useUpdateBranchContactMutation
} from "../branchContactApi";
import {hasPermission} from "../../../helpers/utils/AccessControlUtils";
import ModalButton from "../../../components/common/form/ModalButton";
import {FaCirclePlus, FaGlobe, FaPlus, FaTrash} from "react-icons/fa6";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import {useDeleteBranchContactByIdMutation} from "../branchContactApi";
import AddEditContactForm from "../../../components/common/form/AddEditContactForm";
import {handleRTKQuery} from "../../../helpers/utils/RtkQueryUtils";
import {skipToken} from "@reduxjs/toolkit/query";
import {useLazyGetBranchByIdQuery} from "../branchApi";
import {getEnumDisplayNameByConstant} from "../../../helpers/utils/EnumUtils";
import {useGetEnumsQuery} from "../../../app/globalApi";
import {useGetAllStatesQuery} from "../../data/states/statesApi";

const ContactTab = () => {

    const [deleteContacts, {isLoading}] = useDeleteBranchContactByIdMutation();
    const iconButtonStyle = "cursor-pointer text-lg text-highlight-0";
    const {data: enums} = useGetEnumsQuery("");
    const handleDelete = async (contactId: string) => {
        if (contactId) {
            await deleteContacts(contactId).unwrap();
        }
    };

    let {branchId} = useParams();
    let {customerId} = useParams();
    const {data: stateData} = useGetAllStatesQuery("");

    const [contactId, setContactId] = useState(null);
    const {data: branchContactData} = useGetBranchContactByIdQuery(branchId ?? skipToken);
    const [getBranchById, branchData] = useLazyGetBranchByIdQuery();
    const [show, setShow] = useState(false);
    const [contactData, setContactData] = useState<any>({});
    const [updateContactApi, updateContactApiResponse] = useUpdateBranchContactMutation();
    const [createContactApi, createContactApiResponse] = useCreateBranchContactMutation();
    const [contactByIdApi, contactByIdApiResponse] = useLazyGetBranchContactByContactIdQuery();

    useEffect(() => {
        if (branchId && customerId) {
            getBranchById({customerId: customerId, branchId: branchId})
        }
    }, [branchId]);

    const handleRowClick = (contactId: any) => {
        setContactId(contactId);
        contactByIdApi(contactId);
        setShow(true);
    };

    const handleSubmit = async (e: any) => {
        const isCreate = !contactId;

        const requestBody = {
            contact: {
                id: isCreate ? null : contactData?.id,
                name: contactData?.name,
                email: contactData?.email,
                website: contactData?.website,
                phone: contactData?.phone,
                fax: contactData?.fax,
                preferredContactMethod: contactData?.preferredContactMethod?.value || null,
                isPrimary: contactData?.isPrimary,
                note: contactData?.note,
                address: {
                    ...contactData?.address,
                    state: contactData?.address?.state?.value || null
                }
            },
            shouldNotify: contactData?.shouldNotify,
            type: null,
            tenant: branchData?.data?.tenant,
            branchId: branchId,
        };

        await handleRTKQuery(
            async () => {
                if (isCreate) {
                    return await createContactApi({
                        branchId: branchId,
                        requestBody,
                    }).unwrap();
                } else {
                    return await updateContactApi({
                        contactId,
                        branchId,
                        requestBody,
                    }).unwrap();
                }
            },
            (isSuccess: boolean) => {
                if (isSuccess) {
                    setContactData({});
                    setShow(false);
                    setContactId(null);
                }
            }
        );
    };

    useEffect(() => {
        const contact = contactByIdApiResponse?.data?.contact;

        if (contact) {
            const preferredContactMethod = contact.preferredContactMethod
                ? {
                    value: contact.preferredContactMethod,
                    label: getEnumDisplayNameByConstant(
                        enums?.["ContactMethod"],
                        contact.preferredContactMethod
                    ),
                }
                : null;

            const stateValue = contact.address?.state;

            const address = {
                ...contact.address,
                state: stateValue
                    ? {
                        label: stateData?.find((el: any) => el.id === stateValue)?.stateName,
                        value: stateValue,
                    }
                    : null,
            };

            setContactData({
                ...contact,
                preferredContactMethod,
                address,
                shouldNotify: contactByIdApiResponse?.data?.shouldNotify
            });
        }
    }, [contactByIdApiResponse, enums, stateData]);


    return (
        <>
            <div>
                <table className="w-full">
                    <thead className="bg-highlight-5 text-text-0">
                    <tr>
                        <th className="px-4 py-2 text-left">Name</th>
                        <th className="px-4 py-2 text-left">Phone</th>
                        <th className="px-4 py-2 text-left">Email</th>
                        <th className="px-4 py-2 text-left">Website</th>
                        <th className="px-4 py-2 text-left">Address</th>
                        <th className="px-4 py-2 text-left">
                            {hasPermission(["BRAN-U"]) &&
                                <ModalButton
                                    triggerButton={<FaCirclePlus className={"cursor-pointer text-lg"}
                                                                 title={"Add Contact"}/>}
                                    modal={
                                        <AddEditContactForm
                                            formType={"Add"}
                                            show={true}
                                            setShow={setShow}
                                            onSubmit={() => handleSubmit(contactData)}
                                            setContactData={setContactData}
                                            contactData={contactData}
                                            isLoading={createContactApiResponse.isLoading}
                                            overrideDismissMethod={() => {
                                                setContactData({});
                                            }}
                                            isBranchDetail={true}
                                        />
                                    }
                                />}
                        </th>
                    </tr>
                    </thead>

                    <tbody className="table-striped">
                    {branchContactData?.length === 0 ? (
                        <tr>
                            <td colSpan={10} className="text-gray-500 p-2 bg-text-1">No data available</td>
                        </tr>
                    ) : (
                        branchContactData?.map((row: any) => (
                            <tr key={row.key}
                                className="odd:bg-text-1 hover:bg-text-1 transition cursor-pointer">

                                <td
                                    className="p-2 cursor-pointer text-highlight-4 hover:underline"
                                    onClick={() => handleRowClick(row?.id)}>
                                    {row?.contact?.name}
                                </td>
                                <td className="px-4 py-2">{row?.contact?.phone}</td>
                                <td className="px-4 py-2">
                                    <div className="fa_icon_btn_center_align">
                                            <span className={row?.contact?.email !== null ? "fa fa-envelope" : ""}
                                                  title={row?.contact?.email !== null ? row?.contact?.email : ""}/>
                                    </div>
                                </td>
                                <td className="px-4 py-2">
                                    {row?.contact?.website && (
                                        <a
                                            href={row.contact.website.startsWith("http") ? row.contact.website : `https://${row.contact.website}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-1  text-highlight-0 hover:underline"
                                        >
                                            <FaGlobe className="text-sm"/>

                                        </a>
                                    )}
                                </td>
                                <td className="px-4 py-2">{row?.contact?.address?.mailingAddress}</td>
                                <td className="px-4 py-2">
                                    {hasPermission(["BRAN-U"]) && <ModalButton
                                        triggerButton={<FaTrash className={iconButtonStyle}
                                                                title={"Delete Note"}/>}
                                        modal={
                                            <ConfirmationModal
                                                show={true}
                                                setShow={() => {
                                                }}
                                                title={"Delete Contact"}
                                                bodyMessage={"Are you sure you want to delete this contact?"}
                                                onSubmit={() => handleDelete(row.id)}
                                                isLoading={isLoading}
                                            />
                                        }
                                    />}
                                </td>
                            </tr>
                        ))
                    )}
                    </tbody>

                </table>

                {show && (
                    <AddEditContactForm
                        formType={"Update"}
                        isReadOnly={!hasPermission(["BRAN-U"])}
                        show={true}
                        setShow={setShow}
                        setContactData={setContactData}
                        contactData={contactData}
                        onSubmit={() => handleSubmit(contactData)}
                        isLoading={updateContactApiResponse.isLoading}
                        overrideDismissMethod={() => {
                            setContactId(null);
                            setContactData({});
                        }}
                        isBranchDetail={true}
                    />
                )}
            </div>


        </>

    );
}

export default ContactTab;